import { Autocomplete, TextField, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import type { Theme } from '@mui/material/styles/createTheme';
import SvgIcon from '@mui/material/SvgIcon';
import useMediaQuery from '@mui/material/useMediaQuery';
import { alpha } from '@mui/system/colorManipulator';
import Menu01Icon from '@untitled-ui/icons-react/build/esm/Menu01';
import moment from 'moment';
import { useEffect, useState, type FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { BootstrapDialog } from 'src/components/common-modals/BootstrapDialog';
import PageLeaveConfirmModal from 'src/components/common-modals/page-leave-confirmation';
import useCurrentUser from 'src/hooks/use-current-user';
import { useStopwatch } from 'src/layouts/check-in-checkout/StopwatchContext';
import ClockInFacilityModal from 'src/pages/therapist-dashboard/time-in-out/clock-in-facility-modal';
import { paths } from 'src/paths';
import { resetDailySessionPatientList } from 'src/redux/actions/dailySessionNotes';
import { resetNavigationPath, resetSignatureAccess } from 'src/redux/actions/documentationAction';
import {
  getPayComeTherapistDetails,
  getTherapistProfileAction,
  resetTherapistProfile,
  switchFacilityAction,
  switchFacilityResetAction,
} from 'src/redux/actions/facilityActions';
import {
  getTimeCheckInAction,
  getTimeCheckInActionReset,
  postCheckInOutActionReset,
  postTimeCheckOutAction,
  resetHardClockIn,
} from 'src/redux/actions/therapist-dashboard-action';
import { keycloak, logoutUser } from 'src/routes/AuthContext';
import {
  convertDateToFacilityDate,
  convertDateToSelectedTimeZone,
  convertIntoIsoFormat,
} from 'src/utils/convertDateToSelectedTimeZone';
import { AccountButton } from '../account-button';
import { SearchButton } from '../search-button';

interface TopNavProps {
  onMobileNavOpen?: () => void;

  isSideNav: boolean;
}

export const TopNav: FC<TopNavProps> = (props) => {
  const { onMobileNavOpen, isSideNav, ...other } = props;
  const TOP_NAV_HEIGHT = 64;
  const SIDE_NAV_WIDTH = isSideNav ? 280 : 50;
  const lgUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'));
  const isPhysician = keycloak.tokenParsed?.realm_access?.roles.includes('physician');
  const dispatch = useDispatch();
  const { globalLocationType } = useStopwatch();
  const navigate = useNavigate();
  const getAssignedFacilities = useSelector((state: any) => state.getFacilityDropDownReducer);
  const preventNavigation = useSelector((state: any) => state.preventNavigationReducer);
  const getFacilityUuid = localStorage.getItem('facilityId') || '';
  const [selectedFacility, setSelectedFacility] = useState<any>({});
  const updateFacilityResponse = useSelector(
    (state: any) => state?.updateFacilityDropDownReducer?.isUpdatedFacilities
  );
  const [providerId] = useCurrentUser(['providerId']);
  const getTimeCheckIn = useSelector((state: any) => state.checkInReducer);
  type Option = {
    name: string;
    uuid: string;
  };

  const [isSwitchFacility, setIsSwitchFacility] = useState(false);
  const [selectedSwitchFacility, setSelectedSwitchFacility] = useState<any>('');
  const { redirectSupervisor } = paths;
  const facilityNameLength = selectedFacility?.name?.length || 0;
  const handleChangeFacility = (value: any) => {
    if (!value) return;
    localStorage.removeItem('isCheckIn');
    localStorage.removeItem('lastRunningTime');
    const getFacilityTimeZone = localStorage.getItem('facility_timezone') || '';
    if (getTimeCheckIn?.withPersistResponse?.data?.lastTimerStatus === 'CLOCK_IN') {
      setSelectedSwitchFacility(value);
      setIsSwitchFacility(true);
      return;
    }
    setSelectedFacility(value);
    dispatch(resetDailySessionPatientList());

    dispatch(switchFacilityAction(value.uuid));
  };

  useEffect(() => {
    if (updateFacilityResponse) {
      dispatch(resetHardClockIn());
      const currentDate = new Date();
      const formattedDate = moment(currentDate).format('YYYY-MM-DD');
      const dateTimeAccordingToFacility = convertDateToFacilityDate(currentDate);
      const getFacilityTimeZone = localStorage.getItem('facility_timezone') || '';
      dispatch(postCheckInOutActionReset());
      dispatch(getTimeCheckInActionReset());
      dispatch(resetTherapistProfile());
      dispatch(resetSignatureAccess());
      dispatch(getTherapistProfileAction());
      dispatch(getPayComeTherapistDetails());
      // dispatch(
      //   getAllTaskOverview({
      //     providerId: providerId,
      //     date: formattedDate,
      //   })
      // );

      dispatch(
        getTimeCheckInAction({
          providerId: providerId,
          date: convertIntoIsoFormat(dateTimeAccordingToFacility),
          zoneTime: getFacilityTimeZone,
        })
      );
      // dispatch(
      //   getAllTodaysDocumentAction({
      //     providerId: providerId,
      //     param: {
      //       page: 0,
      //       pageSize: 20,
      //     },
      //   })
      // );
      // dispatch(
      //   getTherapistDashboardPatientList({
      //     providerId: providerId,
      //     date: formattedDate,
      //   })
      // );
      dispatch(switchFacilityResetAction());
      navigate(redirectSupervisor);
    }
  }, [updateFacilityResponse]);

  const handleCloseFacilityModal = () => {
    setSelectedFacility(selectedSwitchFacility);
    dispatch(resetDailySessionPatientList());
    dispatch(switchFacilityAction(selectedSwitchFacility?.uuid));
    setIsSwitchFacility(false);
  };

  const cancelModalSwitch = () => {
    setIsSwitchFacility(false);
  };
  const confirmHandleSwitchModal = () => {
    const getFacilityTimeZone = localStorage.getItem('facility_timezone') || '';
    const currentTime = new Date();
    const dateTimeAccordingToFacility = convertDateToFacilityDate(currentTime);
    const getActivityType = localStorage.getItem('activityType') || '';
    dispatch(
      postTimeCheckOutAction({
        providerId,
        timer: 'CLOCK_OUT',
        date: convertIntoIsoFormat(dateTimeAccordingToFacility),
        zoneId: getFacilityTimeZone,
        clockId: getTimeCheckIn?.withPersistResponse?.data?.uuid,
        isLogout: false,
        idleTimeExpired: false,
        clockInBody: {
          locationType: globalLocationType,
          payByMilage: null,
          clockInActivityType: getActivityType ? getActivityType : null,
        },
      })
    );
    setSelectedFacility(selectedSwitchFacility);
    dispatch(resetDailySessionPatientList());
    dispatch(switchFacilityAction(selectedSwitchFacility?.uuid));
    setIsSwitchFacility(false);
  };

  const getDefaultFacility = () => {
    const facility = getAssignedFacilities.response?.data?.data?.find((fac: any) => {
      if (fac.uuid === getFacilityUuid) {
        return fac;
      }
    });

    setSelectedFacility(facility);
    return facility || {};
  };

  useEffect(() => {
    getDefaultFacility();
  }, [getAssignedFacilities]);

  useEffect(() => {
    const checkTokenExpiration = () => {
      if (keycloak.authenticated) {
        const tokenParsed = keycloak.tokenParsed;

        if (tokenParsed?.session_state && tokenParsed?.iat) {
          if (isPhysician) {
            return;
          }
          const now = Math.floor(new Date().getTime() / 1000);
          // @ts-ignore
          let expirationTime: any = tokenParsed?.exp - now;
          let expirationThreshold = 10;

          const currentTimes = new Date();
          const dateTimeAccordingToFacility = convertDateToFacilityDate(currentTimes);
          const convertToSelectTimeZon = new Date(convertDateToSelectedTimeZone(currentTimes));
          const getFacilityTimeZone = localStorage.getItem('facility_timezone') || '';
          const getActivityType = localStorage.getItem('activityType') || '';
          // const

          if (
            expirationTime == expirationThreshold &&
            localStorage.getItem('isCheckIn') === 'true'
          ) {
            localStorage.removeItem('isCheckIn');
            localStorage.removeItem('lastRunningTime');
            // dispatch(
            //   postTimeCheckOutAction({
            //     providerId,
            //     date: new Date().toISOString(),
            //     timer: 'CLOCK_OUT',
            //     clockId: getTimeCheckIn?.withPersistResponse?.data?.uuid,
            //     isLogout: false,
            //     idleTimeExpired: true,
            //     clockInBody: {
            //       locationType: localStorage.getItem('globalLocationType'),
            //       payByMilage: null,
            //     },
            //   })
            // );
          }
          if (expirationTime === -5) {
            logoutUser();
          }

          if (
            convertToSelectTimeZon.getHours() === 23 &&
            convertToSelectTimeZon.getMinutes() === 59 &&
            convertToSelectTimeZon.getSeconds() === 0 &&
            localStorage.getItem('isCheckIn') === 'true'
          ) {
            dispatch(
              postTimeCheckOutAction({
                providerId,
                date: convertIntoIsoFormat(dateTimeAccordingToFacility),
                zoneId: getFacilityTimeZone,
                timer: 'CLOCK_OUT',
                clockId: getTimeCheckIn?.withPersistResponse?.data?.uuid,
                isLogout: false,
                isAutoClockOut: true,
                idleTimeExpired: false,
                clockInBody: {
                  locationType: localStorage.getItem('globalLocationType'),
                  payByMilage: null,
                  clockInActivityType: getActivityType ? getActivityType : null,
                },
              })
            );
          }
        }
      }
    };

    const tokenExpirationInterval = setInterval(checkTokenExpiration, 1000);

    return () => clearInterval(tokenExpirationInterval);
  }, [providerId]);

  const getTextWidth = (text: string): number => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (context) {
      context.font = 'normal 14px Roboto';
      return context.measureText(text).width;
    }
    return 0;
  };

  const getMaxItemWidth = (options: any) => {
    let maxWidth = 0;
    options.forEach((option: any) => {
      // const optionWidth = option?.name?.length * 8;
      const optionWidth = getTextWidth(option?.name);
      if (optionWidth > maxWidth) {
        maxWidth = optionWidth;
      }
    });
    return maxWidth + 150;
  };

  const [maxWidth, setMaxWidth] = useState(220);

  const options = getAssignedFacilities.response?.data?.data || [];

  useEffect(() => {
    const newMaxWidth = getMaxItemWidth(options);
    setMaxWidth(Math.max(newMaxWidth, 220));
  }, [options]);

  const handleCancelNavigation = () => {
    dispatch(resetNavigationPath());
  };

  const handleNavigation = () => {
    navigate(preventNavigation?.navigateTo);
    dispatch(resetNavigationPath());
  };

  return (
    <Box
      component="header"
      sx={{
        backdropFilter: 'blur(6px)',
        backgroundColor: (theme) => alpha(theme.palette.background.default, 0.8),
        position: 'sticky',
        left: {
          lg: `${SIDE_NAV_WIDTH}px`,
        },
        top: 0,
        width: {
          lg: `calc(100% - ${SIDE_NAV_WIDTH}px)`,
        },
        zIndex: '1000',
      }}
      {...other}
    >
      <Stack
        alignItems="center"
        direction="row"
        justifyContent="space-between"
        spacing={2}
        sx={{
          minHeight: TOP_NAV_HEIGHT,
          px: 2,
        }}
      >
        <Stack
          alignItems="center"
          direction="row"
          spacing={2}
          mt={2}
        >
          {!lgUp && (
            <IconButton onClick={onMobileNavOpen}>
              <SvgIcon>
                <Menu01Icon />
              </SvgIcon>
            </IconButton>
          )}
          <SearchButton />
        </Stack>
        <Stack
          alignItems="center"
          direction="row"
          spacing={2}
        >
          {!isPhysician ? (
            <Autocomplete
              getOptionLabel={(option: Option) => option.name}
              style={{ minWidth: 220, width: maxWidth }}
              fullWidth
              renderOption={(props, option: any, state) => (
                <Typography
                  {...props}
                  noWrap
                >
                  {option?.name}
                </Typography>
              )}
              options={getAssignedFacilities.response?.data?.data || []}
              onChange={(event, value) => handleChangeFacility(value)}
              value={
                selectedFacility && Object.keys(selectedFacility).length !== 0
                  ? selectedFacility
                  : { name: '' }
              }
              renderInput={(params): JSX.Element => (
                <TextField
                  {...params}
                  fullWidth
                  label="Facilities"
                  name="country"
                />
              )}
            />
          ) : null}

          {/* <LanguageSwitch /> */}
          {/* <NotificationsButton /> */}
          {/* <ContactsButton /> */}
          <AccountButton />
        </Stack>
      </Stack>
      <BootstrapDialog
        PaperProps={{ style: { maxWidth: '750px' } }}
        onClose={cancelModalSwitch}
        aria-labelledby="customized-dialog-title"
        open={isSwitchFacility}
      >
        <ClockInFacilityModal
          close={handleCloseFacilityModal}
          handleDelete={confirmHandleSwitchModal}
          selectedFacility={selectedFacility}
        />
      </BootstrapDialog>
      <BootstrapDialog
        onClose={handleCancelNavigation}
        aria-labelledby="customized-dialog-title"
        open={Boolean(preventNavigation?.navigateTo)}
      >
        <PageLeaveConfirmModal
          close={handleCancelNavigation}
          handleSaveBtn={handleNavigation}
        />
      </BootstrapDialog>
    </Box>
  );
};
